import type {
  User,
  Inventory,
  Item,
  ItemWithImages,
  LoginInput,
  RegisterInput,
  CreateInventoryInput,
  CreateItemInput,
  UpdateItemInput,
  ApiResponse,
  PaginatedResponse,
  VoiceSessionResponse,
  EbayConnectionStatus,
  EbayCategory,
  EbayCategoryInfo,
  EbayItemAspect,
  EbayListingSuggestion,
  CreateEbayListingInput,
  EbayListingResponse,
  EbayListing,
  DiscogsConnectionStatus,
  DiscogsRelease,
  CreateDiscogsListingInput,
  DiscogsListingResponse,
  DiscogsListing,
  AiCreditsStatus,
  UserForAdmin,
  UserForAdminWithStats,
  AdminStats,
  GlobalSettings,
  DescriptionPhrase,
} from '@findmystuff/shared';
import { getAccessToken, useAuthStore } from '../store/auth';

const API_BASE = '/api/v1';

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data: ApiResponse<T> = await response.json();

  if (!response.ok) {
    // Handle 401 - logout user
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    throw new Error(data.error?.message || 'Request failed');
  }

  return data.data as T;
}

export const api = {
  auth: {
    login: (input: LoginInput) =>
      request<{ user: User; accessToken: string; refreshToken: string }>(
        '/auth/login',
        { method: 'POST', body: JSON.stringify(input) }
      ),

    register: (input: RegisterInput) =>
      request<{ user: User; accessToken: string; refreshToken: string }>(
        '/auth/register',
        { method: 'POST', body: JSON.stringify(input) }
      ),

    logout: () => request<void>('/auth/logout', { method: 'POST' }),

    forgotPassword: (email: string) =>
      request<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    resetPassword: (token: string, password: string) =>
      request<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }),
  },

  users: {
    me: () => request<User>('/users/me'),
    update: (data: Partial<User>) =>
      request<User>('/users/me', { method: 'PATCH', body: JSON.stringify(data) }),
  },

  inventories: {
    list: () => request<Inventory[]>('/inventories'),

    get: (id: string) => request<Inventory>(`/inventories/${id}`),

    create: (input: CreateInventoryInput) =>
      request<Inventory>('/inventories', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    update: (id: string, data: Partial<Inventory>) =>
      request<Inventory>(`/inventories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<void>(`/inventories/${id}`, { method: 'DELETE' }),
  },

  items: {
    list: (params?: Record<string, string | number | boolean>) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.set(key, String(value));
          }
        });
      }
      const query = searchParams.toString();
      return request<PaginatedResponse<ItemWithImages>>(`/items${query ? `?${query}` : ''}`);
    },

    getLocations: (inventoryId?: string) => {
      const query = inventoryId ? `?inventoryId=${inventoryId}` : '';
      return request<string[]>(`/items/locations${query}`);
    },

    get: (id: string) => request<ItemWithImages>(`/items/${id}`),

    create: (input: CreateItemInput) =>
      request<Item>('/items', { method: 'POST', body: JSON.stringify(input) }),

    update: (id: string, input: UpdateItemInput) =>
      request<Item>(`/items/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    delete: (id: string) => request<void>(`/items/${id}`, { method: 'DELETE' }),

    generateImage: (id: string) =>
      request<{ message: string }>(`/items/${id}/generate-image`, {
        method: 'POST',
      }),

    deleteImage: (id: string) =>
      request<{ message: string }>(`/items/${id}/image`, {
        method: 'DELETE',
      }),

    createManual: (
      inventoryId: string,
      name: string,
      description: string | undefined,
      location: string | undefined,
      locationImage: { data: string; mimeType: string } | undefined,
      condition: string | undefined,
      images: Array<{ data: string; mimeType: string }>
    ) =>
      request<{ id: string; title: string }>('/items/manual', {
        method: 'POST',
        body: JSON.stringify({ inventoryId, name, description, location, locationImage, condition, images }),
      }),

    addImages: (itemId: string, images: Array<{ data: string; mimeType: string }>) =>
      request<{ message: string; count: number }>(`/items/${itemId}/images`, {
        method: 'POST',
        body: JSON.stringify({ images }),
      }),

    deleteOneImage: (itemId: string, imageId: string) =>
      request<{ message: string }>(`/items/${itemId}/images/${imageId}`, {
        method: 'DELETE',
      }),

    reorderImages: (itemId: string, imageIds: string[]) =>
      request<{ message: string }>(`/items/${itemId}/images/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ imageIds }),
      }),

    rotateImage: (itemId: string, imageId: string, direction: 'left' | 'right' = 'right') =>
      request<{ url: string; thumbnailUrl: string }>(`/items/${itemId}/images/${imageId}/rotate`, {
        method: 'POST',
        body: JSON.stringify({ direction }),
      }),

    aiCropImage: (itemId: string, imageId: string) =>
      request<{ url: string; thumbnailUrl: string; originalUrl: string | null }>(`/items/${itemId}/images/${imageId}/ai-crop`, {
        method: 'POST',
      }),

    undoAiCrop: (itemId: string, imageId: string) =>
      request<{ url: string; thumbnailUrl: string; originalUrl: string | null }>(`/items/${itemId}/images/${imageId}/undo-crop`, {
        method: 'POST',
      }),

    transfer: (itemId: string, inventoryId: string) =>
      request<{ message: string }>(`/items/${itemId}/transfer`, {
        method: 'POST',
        body: JSON.stringify({ inventoryId }),
      }),

    generateContent: (itemId: string) =>
      request<{ title: string; description: string }>(`/items/${itemId}/generate-content`, {
        method: 'POST',
      }),

    generateShareLink: (itemId: string) =>
      request<{ shareToken: string }>(`/items/${itemId}/share`, {
        method: 'POST',
      }),

    revokeShareLink: (itemId: string) =>
      request<{ revoked: boolean }>(`/items/${itemId}/share`, {
        method: 'DELETE',
      }),

    uploadToImgbb: (itemId: string) =>
      request<{ galleryUrl: string; imageUrls: string[]; uploadedAt: string }>(`/items/${itemId}/imgbb`, {
        method: 'POST',
      }),

    deleteImgbbImages: (itemId: string) =>
      request<{ deleted: boolean }>(`/items/${itemId}/imgbb`, {
        method: 'DELETE',
      }),
  },

  realtime: {
    createSession: (inventoryId: string) =>
      request<VoiceSessionResponse>('/realtime/session', {
        method: 'POST',
        body: JSON.stringify({ inventoryId }),
      }),

    getSession: (id: string) => request<any>(`/realtime/session/${id}`),

    endSession: (id: string) =>
      request<any>(`/realtime/session/${id}/end`, { method: 'POST' }),

    listSessions: () => request<any[]>('/realtime/sessions'),

    analyzeImages: (inventoryId: string, images: Array<{ data: string; mimeType: string }>) =>
      request<{
        items: Array<{ id: string; title: string; category: string; confidence: number }>;
        totalExtracted: number;
      }>('/realtime/analyze-images', {
        method: 'POST',
        body: JSON.stringify({ inventoryId, images }),
      }),
  },

  locations: {
    list: () =>
      request<Array<{ id: string; name: string; imageUrl?: string; createdAt: string; updatedAt: string }>>('/locations'),

    create: (name: string, imageData?: { data: string; mimeType: string }) =>
      request<{ id: string; name: string; imageUrl?: string }>('/locations', {
        method: 'POST',
        body: JSON.stringify({ name, imageData }),
      }),

    delete: (id: string) => request<void>(`/locations/${id}`, { method: 'DELETE' }),
  },

  ebay: {
    getAuthUrl: (siteId?: string) =>
      request<{ authUrl: string; state: string }>(`/ebay/auth/url${siteId ? `?siteId=${siteId}` : ''}`),

    getStatus: () =>
      request<EbayConnectionStatus>('/ebay/auth/status'),

    disconnect: () =>
      request<{ success: boolean }>('/ebay/auth/disconnect', { method: 'POST' }),

    searchCategories: (siteId: string, query: string) =>
      request<EbayCategory[]>(`/ebay/categories?siteId=${siteId}&query=${encodeURIComponent(query)}`),

    getCategoryAspects: (siteId: string, categoryId: string) =>
      request<EbayCategoryInfo>(`/ebay/categories/${categoryId}/aspects?siteId=${siteId}`),

    getListingSuggestions: (
      imageUrls: string[],
      categoryAspects?: EbayItemAspect[],
      existingTitle?: string,
      existingDescription?: string
    ) =>
      request<EbayListingSuggestion>('/ebay/listings/suggestions', {
        method: 'POST',
        body: JSON.stringify({ imageUrls, categoryAspects, existingTitle, existingDescription }),
      }),

    createListing: (input: CreateEbayListingInput) =>
      request<EbayListingResponse>('/ebay/listings', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    getListings: (itemId: string) =>
      request<EbayListing[]>(`/ebay/listings/${itemId}`),
  },

  discogs: {
    getAuthUrl: () =>
      request<{ authUrl: string }>('/discogs/auth/url'),

    getStatus: () =>
      request<DiscogsConnectionStatus>('/discogs/auth/status'),

    disconnect: () =>
      request<{ success: boolean }>('/discogs/auth/disconnect', { method: 'POST' }),

    searchReleases: (query: string) =>
      request<DiscogsRelease[]>(`/discogs/releases?query=${encodeURIComponent(query)}`),

    createListing: (input: CreateDiscogsListingInput) =>
      request<DiscogsListingResponse>('/discogs/listings', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    getListings: (itemId: string) =>
      request<DiscogsListing[]>(`/discogs/listings/${itemId}`),

    updateListing: (itemId: string, data: {
      mediaCondition: string;
      sleeveCondition: string;
      price: number;
      comments?: string;
    }) =>
      request<{ success: boolean }>(`/discogs/listings/${itemId}/update`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    deleteListing: (itemId: string) =>
      request<{ success: boolean }>(`/discogs/listings/${itemId}`, { method: 'DELETE' }),

    getPriceSuggestions: (releaseId: number) =>
      request<Array<{ condition: string; value: number | null; currency: string }>>(
        `/discogs/releases/${releaseId}/price-suggestions`
      ),

    getRelease: (releaseId: number) =>
      request<DiscogsRelease>(`/discogs/releases/${releaseId}`),

    syncSales: () =>
      request<{ synced: number; total: number }>('/discogs/sync-sales', { method: 'POST' }),

    getItemOrderInfo: (itemId: string) =>
      request<{
        messages: Array<{
          subject: string;
          message: string;
          from: { username: string };
          timestamp: string;
          type: 'message' | 'status' | 'shipping';
        }>;
        buyer?: {
          username: string;
          email?: string;
        };
        orderId?: string;
        orderTotal?: number;
        shippingAddress?: string;
        shipping?: {
          status: string;
          trackingNumber?: string;
          trackingCarrier?: string;
          shippedAt?: string;
        };
      }>(`/discogs/items/${itemId}/messages`),

    getAllMessages: () =>
      request<Array<{
        orderId: string;
        itemId: string;
        itemTitle: string;
        itemThumbnail?: string;
        buyer: { username: string };
        latestMessage: {
          message: string;
          from: string;
          timestamp: string;
          type: string;
        };
        messageCount: number;
        hasUnread: boolean;
      }>>('/discogs/messages'),

    getUnreadCount: () =>
      request<{ count: number }>('/discogs/messages/unread-count'),

    sendMessage: (orderId: string, message: string, subject?: string) =>
      request<{ success: boolean }>(`/discogs/orders/${orderId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message, subject }),
      }),

    // Mark order as shipped on Discogs with optional tracking info
    updateOrderShipping: (orderId: string, trackingNumber?: string, carrierName?: string) =>
      request<{ success: boolean }>(`/discogs/orders/${orderId}/ship`, {
        method: 'POST',
        body: JSON.stringify({ trackingNumber, carrierName }),
      }),
  },

  // Public endpoints (no auth required)
  public: {
    getSharedItem: (token: string) =>
      request<{
        id: string;
        title: string;
        description: string;
        condition: string;
        category: string;
        images: Array<{
          id: string;
          url: string;
          thumbnailUrl: string;
        }>;
      }>(`/public/share/${token}`),
  },

  // Admin & AI Credits
  admin: {
    // AI Credits (for all users)
    getCreditsStatus: () =>
      request<AiCreditsStatus>('/admin/credits/status'),

    useCredit: () =>
      request<AiCreditsStatus>('/admin/credits/use', { method: 'POST' }),

    checkAdmin: () =>
      request<{ isAdmin: boolean }>('/admin/check'),

    // Admin-only endpoints
    getStats: () =>
      request<AdminStats & { defaultAiCreditsLimit: number }>('/admin/stats'),

    getUsers: (params?: { search?: string; page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.set('search', params.search);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
      const query = searchParams.toString();
      return request<{ users: UserForAdminWithStats[]; meta: { page: number; pageSize: number; totalCount: number; totalPages: number } }>(
        `/admin/users${query ? `?${query}` : ''}`
      );
    },

    updateUserCredits: (userId: string, aiCreditsLimit: number | null) =>
      request<UserForAdmin>('/admin/users/credits', {
        method: 'PATCH',
        body: JSON.stringify({ userId, aiCreditsLimit }),
      }),

    resetUserCredits: (userId: string) =>
      request<UserForAdmin>(`/admin/users/${userId}/reset-credits`, { method: 'POST' }),

    updateUserAdmin: (userId: string, isAdmin: boolean) =>
      request<UserForAdmin>('/admin/users/admin', {
        method: 'PATCH',
        body: JSON.stringify({ userId, isAdmin }),
      }),

    getSettings: () =>
      request<GlobalSettings>('/admin/settings'),

    updateSettings: (defaultAiCreditsLimit: number) =>
      request<GlobalSettings>('/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify({ defaultAiCreditsLimit }),
      }),

    downloadBackup: async () => {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/admin/backup`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to download backup');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || 'backup.sql';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  },

  // Email Integration
  email: {
    // Gmail
    getGmailAuthUrl: () =>
      request<{ authUrl: string; state: string }>('/gmail/auth/url'),

    getGmailStatus: () =>
      request<{
        configured: boolean;
        connected: boolean;
        email?: string;
        lastSyncAt?: string | null;
        emailCount?: number;
        linkedCount?: number;
      }>('/gmail/auth/status'),

    disconnectGmail: () =>
      request<{ success: boolean }>('/gmail/auth/disconnect', { method: 'POST' }),

    syncGmail: () =>
      request<{ synced: number; linked: number; errors: number }>('/gmail/sync', { method: 'POST' }),

    // Outlook
    getOutlookAuthUrl: () =>
      request<{ authUrl: string; state: string }>('/outlook/auth/url'),

    getOutlookStatus: () =>
      request<{
        configured: boolean;
        connected: boolean;
        email?: string;
        lastSyncAt?: string | null;
        emailCount?: number;
        linkedCount?: number;
      }>('/outlook/auth/status'),

    disconnectOutlook: () =>
      request<{ success: boolean }>('/outlook/auth/disconnect', { method: 'POST' }),

    syncOutlook: () =>
      request<{ synced: number; linked: number; errors: number }>('/outlook/sync', { method: 'POST' }),

    // General email endpoints
    getAccounts: () =>
      request<Array<{
        id: string;
        provider: string;
        email: string;
        lastSyncAt: string | null;
        emailCount: number;
        linkedCount: number;
      }>>('/email/accounts'),

    getEmailsForItem: (itemId: string) =>
      request<Array<{
        id: string;
        subject: string;
        fromEmail: string;
        fromName: string | null;
        snippet: string | null;
        bodyText: string | null;
        bodyHtml: string | null;
        receivedAt: string;
        isRead: boolean;
        provider: string;
      }>>(`/email/items/${itemId}`),

    getAllEmails: (params?: { page?: number; pageSize?: number; linkedOnly?: boolean }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
      if (params?.linkedOnly) searchParams.set('linkedOnly', 'true');
      const query = searchParams.toString();
      return request<{
        emails: Array<{
          id: string;
          subject: string;
          fromEmail: string;
          fromName: string | null;
          snippet: string | null;
          receivedAt: string;
          isRead: boolean;
          discogsOrderId: string | null;
          provider: string;
          accountEmail: string;
          linkedItem: { id: string; title: string } | null;
        }>;
        meta: { page: number; pageSize: number; totalCount: number; totalPages: number };
      }>(`/email${query ? `?${query}` : ''}`);
    },

    markAsRead: (emailId: string) =>
      request<{ success: boolean }>(`/email/${emailId}/read`, { method: 'PATCH' }),

    syncAll: () =>
      request<{
        gmail: { synced: number; linked: number; errors: number } | null;
        outlook: { synced: number; linked: number; errors: number } | null;
      }>('/email/sync', { method: 'POST' }),

    relinkEmails: () =>
      request<{ linked: number }>('/email/relink', { method: 'POST' }),
  },

  // Description Phrases
  phrases: {
    list: () =>
      request<DescriptionPhrase[]>('/phrases'),

    create: (phrase: string) =>
      request<DescriptionPhrase>('/phrases', {
        method: 'POST',
        body: JSON.stringify({ phrase }),
      }),

    delete: (id: string) =>
      request<void>(`/phrases/${id}`, { method: 'DELETE' }),

    incrementUsage: (id: string) =>
      request<{ success: boolean }>(`/phrases/${id}/use`, { method: 'POST' }),
  },
};
